/**
 * AuthContext with Required Google OAuth Authentication
 * All users must sign in - no guest access allowed
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api.js';
import { API_CONFIG } from '../config/api.js';
import { clearAllQueries, invalidateUserQueries } from '../lib/queryClient.js';
import { getDeviceFingerprint, storeDeviceFingerprintCookie, clearDeviceFingerprintCookie } from '../utils/deviceFingerprint.js';
import { createLogger } from '../utils/logger.js';

const log = createLogger('Auth');
const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authType, setAuthType] = useState('unauthenticated'); // 'unauthenticated', 'authenticated'

    // Initialize authentication on app load
    const initializationRef = useRef(false);
    
    useEffect(() => {
        if (!initializationRef.current) {
            initializationRef.current = true;
            initializeAuth();
        } else {
        }
    }, []);

    // 🔧 OPTIMIZED: Validate session only on tab focus (if away > 5 min)
    // Removed 60s polling - unnecessary API calls
    useEffect(() => {
        if (!user || loading || authType !== 'authenticated') return;

        let lastActiveTime = Date.now();
        const INACTIVE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const timeSinceActive = Date.now() - lastActiveTime;

                // Only validate if user was away for more than 5 minutes
                if (timeSinceActive > INACTIVE_THRESHOLD) {
                    log.log('Tab focused after inactivity, validating session...');
                    const sessionValid = await validateExistingSession();
                    if (!sessionValid) {
                        await signOut();
                        return;
                    }
                }
            } else {
                // Tab hidden - record the time
                lastActiveTime = Date.now();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, authType, loading]);

    // 🔐 Listen for session_expired events from API client
    // Auto-logout when backend rejects session with 401/403
    useEffect(() => {
        const handleSessionExpired = async (event) => {
            log.log('Session expired event received:', event.detail);

            // Show user-friendly message
            if (typeof window.showToast === 'function') {
                window.showToast('Your session has expired. Please sign in again.', 'warning');
            }

            // Small delay to let toast show before redirect
            await new Promise(resolve => setTimeout(resolve, 500));

            // Trigger logout
            await signOut();
        };

        window.addEventListener('session_expired', handleSessionExpired);

        return () => {
            window.removeEventListener('session_expired', handleSessionExpired);
        };
    }, []);

    const initializeAuth = async () => {
        try {
            setLoading(true);
            
            // Check for OAuth callback parameters first
            const oauthSuccess = await handleOAuthCallback();
            if (oauthSuccess) {
                return;
            }
            
            // Try to validate existing session
            const sessionValid = await validateExistingSession();
            
            if (sessionValid) {
                return;
            }
            
            // No valid session - user must sign in
            setUser(null);
            setIsAuthenticated(false);
            setAuthType('unauthenticated');
            
        } catch (error) {
            // No fallback - user must sign in
            setUser(null);
            setIsAuthenticated(false);
            setAuthType('unauthenticated');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle OAuth callback parameters from URL
     * 🔒 SECURITY: Uses one-time token exchange instead of email in URL
     */
    const handleOAuthCallback = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const oauthToken = urlParams.get('oauth_token');

            // Handle OAuth callback with secure token exchange
            if (oauthToken) {

                // Clear URL parameters immediately for security
                window.history.replaceState({}, document.title, window.location.pathname);

                // Clear device fingerprint cookie - no longer needed after OAuth
                clearDeviceFingerprintCookie();

                // 🔒 SECURITY: Exchange one-time token for user info (no PII in URL)
                let userEmail, userName, referralApplied;
                try {
                    const response = await fetch(API_CONFIG.endpoints.auth.oauthResult, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ token: oauthToken })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.success) {
                            userEmail = data.email;
                            userName = data.name;
                            referralApplied = data.referralApplied;
                        } else {
                            log.error('OAuth token exchange failed:', data.error);
                            return false;
                        }
                    } else {
                        log.error('OAuth token exchange failed:', response.status);
                        return false;
                    }
                } catch (exchangeError) {
                    log.error('OAuth token exchange error:', exchangeError);
                    return false;
                }

                // 🚀 OPTIMISTIC UI: Set user state immediately
                // This gives instant feedback while session validates in background
                setUser({
                    email: userEmail,
                    displayName: userName || userEmail.split('@')[0],
                    subscriptionTier: 'free', // Will be updated after validation
                    plan: 'free',
                    credits: 0 // Will be updated after validation
                });
                setIsAuthenticated(true);
                setAuthType('authenticated');
                setLoading(false); // Stop loading immediately

                // Show referral bonus notification (only if referral was applied)
                if (referralApplied) {
                    setTimeout(() => window.showToast?.('You received 8 bonus credits from referral!', 'success'), 100);
                }

                // Validate session in background and update with full data
                validateExistingSession().then(valid => {
                    if (!valid) {
                        // Session invalid - force re-login
                        setUser(null);
                        setIsAuthenticated(false);
                        setAuthType('unauthenticated');
                    }
                });

                return true;
            }

            return false;

        } catch (error) {
            return false;
        }
    };

    /**
     * Try to validate existing session with backend using secure cookies
     */
    const validateExistingSession = async () => {
        try {
            // Validate session with backend using secure httpOnly cookies
            // Note: Can't check document.cookie for httpOnly cookies - just make the API call
            const sessionCheck = await fetch(API_CONFIG.endpoints.auth.session, {
                method: 'GET',
                credentials: 'include' // Automatically includes httpOnly cookies
            });


            if (sessionCheck.ok) {
                const sessionData = await sessionCheck.json();
                
                if (sessionData.authenticated && sessionData.user) {
                    
                    // Valid authenticated session - use subscriptionTier directly from DB
                    const userPlan = sessionData.user.subscriptionTier || 'free';
                    setUser({
                        id: sessionData.user.id,
                        email: sessionData.user.email,
                        displayName: sessionData.user.displayName,
                        avatarUrl: sessionData.user.avatarUrl,
                        subscriptionTier: userPlan,
                        plan: userPlan,
                        credits: sessionData.user.credits?.available || 0,
                        subscription: sessionData.user.subscription,
                        session: sessionData.session
                    });
                    setIsAuthenticated(true);
                    setAuthType('authenticated');
                    
                    // Update API with user ID for credit tracking
                    api.setCurrentUserId(sessionData.user.id);
                    
                    return true;
                } else {
                }
            } else {
            }
            
            return false;
            
        } catch (error) {
            return false;
        }
    };


    /**
     * Initiate Google OAuth login
     * Passes referral code from URL through OAuth state parameter (HMAC signed)
     * Stores device fingerprint in cookie for backend to read after OAuth redirect
     */
    const signInWithGoogle = async () => {
        try {
            // Generate and store device fingerprint BEFORE OAuth redirect
            // Cookie persists through Google redirect and back to our callback
            const fingerprint = await getDeviceFingerprint();
            storeDeviceFingerprintCookie(fingerprint);
            log.log('Device fingerprint stored for OAuth flow');

            // Check for referral code in current URL
            const urlParams = new URLSearchParams(window.location.search);
            const referralCode = urlParams.get('ref');

            log.log('signInWithGoogle called');
            log.log('Current URL:', window.location.href);
            log.log('URL search params:', window.location.search);
            log.log('Referral code from URL:', referralCode);

            // Build OAuth login URL with optional referral code
            let loginUrl = API_CONFIG.endpoints.auth.googleLogin;
            if (referralCode) {
                loginUrl += `?ref=${encodeURIComponent(referralCode)}`;
                log.log('Passing referral code through OAuth:', referralCode);
            }

            log.log('Redirecting to:', loginUrl);

            // Redirect to OAuth login endpoint on backend
            // Backend will embed ref in HMAC-signed state parameter
            window.location.href = loginUrl;
        } catch (error) {
            throw new Error('Failed to initiate Google sign-in');
        }
    };

    /**
     * Sign out user and clear secure session
     */
    const signOut = async () => {
        try {
            if (authType === 'authenticated') {
                // Call logout endpoint for authenticated users
                try {
                    await fetch(API_CONFIG.endpoints.auth.session, {
                        method: 'DELETE',
                        credentials: 'include', // Uses httpOnly cookies automatically
                        timeout: 10000 // 10 second timeout for logout
                    });
                } catch (logoutError) {
                    // Backend logout failed, continuing with cleanup
                }
            }

            // Comprehensive cleanup of all client-side data

            // Clear API and user data
            api.clearCurrentUser(); // This clears localStorage items

            // Clear session storage
            sessionStorage.clear();

            // Clear TanStack Query cache
            clearAllQueries();

            // Note: localStorage cleanup no longer needed - using secure httpOnly cookies

            // Reset all auth state
            setUser(null);
            setIsAuthenticated(false);
            setAuthType('unauthenticated');

            // Navigate to sign-in page and force reload to clear any cached state
            window.location.href = '/signin';

        } catch (error) {
            // Force complete reset even if API call fails
            api.clearCurrentUser();
            sessionStorage.clear();
            clearAllQueries();

            // Reset auth state
            setUser(null);
            setIsAuthenticated(false);
            setAuthType('unauthenticated');

            // Force navigation and reload
            window.location.href = '/signin';
        }
    };

    /**
     * Refresh user profile and credit balance from backend
     */
    const refreshUserProfile = async () => {
        if (!user?.id) return;
        
        try {
            if (authType !== 'authenticated') {
                return;
            }
            
            
            // Get updated session data and plan limits from backend
            const [sessionResponse, planLimitsResponse] = await Promise.allSettled([
                // Session endpoint for user data
                fetch(API_CONFIG.endpoints.auth.session, {
                    method: 'GET',
                    credentials: 'include' // Uses httpOnly cookies automatically
                }),
                
                // Plan limits endpoint for credit balance
                fetch(`${API_CONFIG.apiURL}/user/plan-limits`, {
                    method: 'GET',
                    credentials: 'include' // Uses httpOnly cookies automatically
                })
            ]);
            
            // Process session data
            if (sessionResponse.status === 'fulfilled' && sessionResponse.value.ok) {
                const sessionData = await sessionResponse.value.json();
                if (sessionData.authenticated && sessionData.user) {
                    // Use subscriptionTier directly from DB
                    const userPlan = sessionData.user.subscriptionTier || 'free';
                    setUser(prev => ({
                        ...prev,
                        email: sessionData.user.email,
                        displayName: sessionData.user.displayName,
                        avatarUrl: sessionData.user.avatarUrl,
                        subscriptionTier: userPlan,
                        plan: userPlan,
                        subscription: sessionData.user.subscription
                    }));
                }
            } else {
                // Session is invalid, force logout
                await signOut();
                return;
            }
            
            // Process plan limits data
            if (planLimitsResponse.status === 'fulfilled' && planLimitsResponse.value.ok) {
                const planData = await planLimitsResponse.value.json();
                if (planData.success && planData.limits) {
                    const planName = planData.limits.currentPlan.name.toLowerCase();
                    setUser(prev => ({
                        ...prev,
                        credits: planData.limits.currentUsage.availableCredits,
                        planLimits: planData.limits,
                        subscriptionTier: planName,
                        // 🔧 FIX: Add plan field for UserMenu.jsx compatibility
                        plan: planName
                    }));
                }
            }

            // Invalidate TanStack Query cache to sync with updated data
            invalidateUserQueries();

        } catch (error) {
            
            // Validate session on error
            const sessionValid = await validateExistingSession();
            if (!sessionValid) {
                await signOut();
            }
        }
    };

    /**
     * Update user credits (for real-time tracking)
     */
    const updateUserCredits = (newCredits) => {
        if (user) {
            setUser(prev => ({
                ...prev,
                credits: newCredits
            }));
        }
    };

    /**
     * Force refresh credit balance from backend (useful after operations)
     */
    const refreshCredits = async () => {
        if (!user?.id || authType !== 'authenticated') {
            return user?.credits || 0;
        }
        
        try {
            // Get from plan limits endpoint
            const planResponse = await fetch(`${API_CONFIG.apiURL}/user/plan-limits`, {
                method: 'GET',
                credentials: 'include' // Uses httpOnly cookies automatically
            });
            
            if (planResponse.ok) {
                const planData = await planResponse.json();
                if (planData.success && planData.limits) {
                    updateUserCredits(planData.limits.currentUsage.availableCredits);
                    return planData.limits.currentUsage.availableCredits;
                }
            }
        } catch (error) {
        }
        
        return user.credits; // Return current credits if refresh failed
    };

    /**
     * Handle plan upgrade
     */
    const upgradePlan = async (planId, paymentMethodId) => {
        if (!user || authType !== 'authenticated') {
            throw new Error('Please sign in to upgrade your plan');
        }
        
        try {
            const result = await api.upgradeSubscription(
                user.id,
                planId,
                paymentMethodId,
                user.email
            );
            
            // Refresh user profile to get updated plan info
            await refreshUserProfile();
            
            return result;
        } catch (error) {
            throw error;
        }
    };

    /**
     * Check if user can perform operation (credit/limit check)
     */
    const canPerformOperation = (operationType, requiredCredits = 1) => {
        if (!user || authType !== 'authenticated') return false;
        
        // Check actual credit balance for authenticated users
        return user.credits >= requiredCredits;
    };

    /**
     * Get authentication status info
     */
    const getAuthInfo = () => {
        return {
            isAuthenticated: authType === 'authenticated',
            isGuest: false, // No guest users allowed
            authType,
            canPurchaseCredits: authType === 'authenticated',
            canUpgradePlan: authType === 'authenticated',
            loginUrl: API_CONFIG.endpoints.auth.googleLogin,
            requiresSignIn: authType === 'unauthenticated'
        };
    };

    /**
     * Helper methods for cleaner component logic
     */
    const canAccessAPI = () => authType === 'authenticated';
    
    const canMakePurchases = () => authType === 'authenticated';
    
    const requiresAuthentication = (redirectTo = '/signin') => {
        if (authType !== 'authenticated') {
            // For now just return false, actual redirect would be handled by components
            return false;
        }
        return true;
    };

    const isReallyAuthenticated = () => authType === 'authenticated' && isAuthenticated;

    const value = {
        // User state
        user,
        loading,
        isAuthenticated,
        authType,
        
        // Authentication methods
        signInWithGoogle,
        signOut,
        
        // Profile management
        refreshUserProfile,
        updateUserCredits,
        refreshCredits,
        
        // Billing operations
        upgradePlan,
        
        // Utility methods
        canPerformOperation,
        getAuthInfo,
        
        // Helper methods for cleaner component logic
        canAccessAPI,
        canMakePurchases,
        requiresAuthentication,
        isReallyAuthenticated,
        
        // Legacy compatibility
        initializeAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;